// First-class loading / error / empty states. Errors surface `error.code` + `message`
// (never swallowed); rate-limited errors show the unlock time.

import type { ReactNode } from "react";
import { Glyph } from "@/ui";
import { ApiError } from "@/lib/api";
import { Alert, Badge, Button, Card } from "@/ui";
import { formatDateTime } from "@/lib/format";

export function Loading({ label = "Memuat…" }: { label?: string }) {
  return (
    <div
      data-slot="loading"
      className="flex items-center gap-3 py-6 text-sm text-fg-2"
      role="status"
      aria-live="polite"
    >
      <Glyph name="spinner" className="size-4 shrink-0 motion-safe:animate-spin" />
      <span>{label}</span>
    </div>
  );
}

// Upstream: shadcn-ui/ui apps/v4/registry/new-york-v4/ui/empty.tsx — MIT (title + body; no media slot).
export function EmptyState({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}) {
  return (
    <Card className="gap-2">
      <p className="font-medium text-fg-0">{title}</p>
      {children && <div className="text-sm text-fg-2">{children}</div>}
    </Card>
  );
}

function isRateLimited(error: unknown): error is ApiError {
  return error instanceof ApiError && error.code === "rate_limited";
}

type ErrorStateProps = {
  error: ApiError | Error;
  onRetry?: () => void;
} & (
  | {
      /** Default: the screen's own content failed. A danger panel, always announced. */
      variant?: "panel";
    }
  | {
      /** A failure inside a card (a card action, a form, a card's own fetch): a danger
       *  Alert, so a Card never nests the danger panel. */
      variant: "inline";
      /** For a form control's aria-describedby. */
      id?: string;
      /** Announce it (role="alert") for errors caused by the user's own action. A card's
       *  background fetch failing leaves the rest of the page usable, so it stays silent. */
      live?: boolean;
    }
);

// Inline variant structure: shadcn-ui/ui apps/v4/registry/new-york-v4/ui/alert.tsx — MIT (via the @/ui Alert).
export function ErrorState(props: ErrorStateProps) {
  const { error, onRetry } = props;
  const code = error instanceof ApiError ? error.code : "error";
  const nextAllowed = isRateLimited(error) ? error.nextAllowedAt : undefined;

  // Both variants say the same thing: code, message, unlock time, optional retry. The
  // unlock line is fg-2 on the panel's white ground; inside the danger-subtle Alert it
  // keeps the Alert's own body colour (fg-2 is not legible on a *-subtle background).
  const details = (
    <>
      <Badge tone="crit" className="font-mono">
        {code}
      </Badge>
      <p className="text-fg-0">{error.message}</p>
      {nextAllowed && (
        <p className={props.variant === "inline" ? undefined : "text-sm text-fg-2"}>
          Coba lagi setelah {formatDateTime(nextAllowed)}.
        </p>
      )}
      {onRetry && (
        <Button variant="quiet" size="sm" onClick={onRetry}>
          Coba lagi
        </Button>
      )}
    </>
  );

  if (props.variant === "inline") {
    return (
      <Alert id={props.id} tone="danger" role={props.live ? "alert" : undefined}>
        <div className="flex flex-col items-start gap-2">{details}</div>
      </Alert>
    );
  }

  return (
    <Card tone="danger" role="alert" className="items-start gap-3">
      {details}
    </Card>
  );
}
